import React, { useEffect, useRef, useState } from "react"

import { NotificationManager } from "react-notifications"
import { gql } from "urql"
import { pipe, subscribe } from "wonka"

import createBlob from "src/createBlob"
import { adminClient as client } from "src/urql-client"

export type ExportButtonsProps = { id: string }

const ExportButtons: React.FC<ExportButtonsProps> = ({ id }) => {
  const [submitting, setSubmitting] = useState(false)
  const [processing, setProcessing] = useState<string | null>(null)
  const [csvUrl, setCsvUrl] = useState<string | null>(null)
  const csvDownloadLink = useRef<HTMLAnchorElement>()

  const handleResultsExport = () => {
    const { unsubscribe } = pipe(
      client.subscription(exportResultsSubscription, { surveyId: id }),
      subscribe(result => {
        if (result.error) {
          NotificationManager.error(result.error?.message)
          setSubmitting(false)
          setProcessing(null)

          return
        }

        const { status, failures, csv } = result.data.exportSurveyResults
        if (failures.length > 0) {
          NotificationManager.error(failures[0].message)

          setSubmitting(false)
          setProcessing(null)

          unsubscribe()
        }

        if (status === "done") {
          const blobUrl = createBlob(csv, "text/csv")
          setCsvUrl(blobUrl)
          setSubmitting(false)
          setProcessing(null)
          unsubscribe()
        } else if (status === "enqueued") {
          setProcessing("results")
        }
      })
    )
  }

  const handleNoResponseExport = () => {
    const { unsubscribe } = pipe(
      client.subscription(exportNoResponseStudentsSubscription, {
        surveyId: id,
      }),
      subscribe(result => {
        if (result.error) {
          NotificationManager.error(result.error?.message)
          setSubmitting(false)
          setProcessing(null)

          return
        }

        const { status, failures, csv } =
          result.data.exportNoSurveyResponseStudents
        if (failures.length > 0) {
          NotificationManager.error(failures[0].message)

          setSubmitting(false)
          setProcessing(null)

          unsubscribe()
        }

        if (status === "done") {
          const blobUrl = createBlob(csv, "text/csv")
          setCsvUrl(blobUrl)
          setSubmitting(false)
          setProcessing(null)
          unsubscribe()
        } else if (status === "enqueued") {
          setProcessing("no response")
        }
      })
    )
  }

  useEffect(() => {
    if (!csvUrl) return
    csvDownloadLink.current.click()
    setCsvUrl(null)
    setTimeout(() => URL.revokeObjectURL(csvUrl), 0)
  }, [csvUrl])

  return (
    <>
      <a
        ref={csvDownloadLink}
        href={csvUrl}
        download={`survey ${processing}.csv`}
        className="hidden"
      >
        csv
      </a>
      <div className="flex space-x-4">
        {processing ? (
          <div className="alert alert-info">
            Your export is processing. Your file will be downloaded when it is
            ready.
          </div>
        ) : (
          <>
            <button
              className="btn btn-success"
              onClick={handleResultsExport}
              disabled={submitting}
            >
              Export Results
            </button>
            <button
              className="btn btn-info"
              onClick={handleNoResponseExport}
              disabled={submitting}
            >
              Export No Response Students
            </button>
          </>
        )}
      </div>
    </>
  )
}

const exportResultsSubscription = gql`
  subscription ExportSurveyResults($surveyId: ID!) {
    exportSurveyResults(surveyId: $surveyId) {
      status
      csv
      failures {
        message
      }
    }
  }
`

const exportNoResponseStudentsSubscription = gql`
  subscription ExportNoSurveyResponseStudents($surveyId: ID!) {
    exportNoSurveyResponseStudents(surveyId: $surveyId) {
      status
      csv
      failures {
        message
      }
    }
  }
`

export default ExportButtons
